'use client';

import { useAppSelector } from 'src/hooks/redux-hooks';
import type { FlagIndicator } from 'src/types/jobs';

// Escalation has its own dedicated banner + pill on the slide-out, so the
// generic flag surfaces (summary banner, header flag-tag) exclude it by its
// customer-facing label to avoid double-surfacing.
const ESCALATED_LABEL = 'Escalated';

// Fallback when a work order is in the flagged-id set but the indicators map
// hasn't shipped/loaded: flagged_work_order_job_ids IS the customer-visible
// (Build Back Risk) rollup set, so this label is the only one it can mean.
const FALLBACK_FLAG_LABEL = 'Build Back Risk';

/**
 * Active-flag labels for the open slide-out, mode-aware (SAAS-815 AC#5).
 *
 * - Work-order mode (`jobDetails`) reads the WO's own rollup
 *   (`GET /api/flags/work-order/{id}` → `state.jobDetails.flagRollup`).
 * - Maintenance-request mode (`serviceRequestDetails`) ROLLS UP from the
 *   shared flag summary (`GET /api/flags/summary` — already fetched by this
 *   panel's jobs table): any active flag on any of the MR's work orders
 *   surfaces at the MR level. The WO-mode `flagRollup` slice is deliberately
 *   NOT read in this mode — it belongs to the previously-opened work order
 *   and would render a stale banner/chip on the MR panel.
 */
export default function useSlideOutFlagLabels(): string[] {
  const type = useAppSelector((state) => state?.jobDetails?.slideOut?.type);
  const flagRollup = useAppSelector((state) => state?.jobDetails?.flagRollup);
  const mrJobs = useAppSelector((state) => state?.jobDetails?.data?.jobs);
  const flaggedWorkOrderJobIds = useAppSelector(
    (state) => state?.flagSummary?.flaggedWorkOrderJobIds,
  );
  const indicators = useAppSelector((state) => state?.flagSummary?.indicators);

  if (type === 'serviceRequestDetails') {
    const rootJobIds = (Array.isArray(mrJobs) ? mrJobs : [])
      .map((row) => Number((row as { root_job_id?: number | string })?.root_job_id))
      .filter((id) => Number.isFinite(id) && id > 0);

    const labels: string[] = [];
    rootJobIds.forEach((rootJobId) => {
      const jobIndicators: FlagIndicator[] = indicators?.[rootJobId] ?? [];
      jobIndicators.forEach((indicator) => {
        const label = indicator?.label;
        if (label && label !== ESCALATED_LABEL && !labels.includes(label)) {
          labels.push(label);
        }
      });
      if (
        jobIndicators.length === 0
        && (flaggedWorkOrderJobIds ?? []).includes(rootJobId)
        && !labels.includes(FALLBACK_FLAG_LABEL)
      ) {
        labels.push(FALLBACK_FLAG_LABEL);
      }
    });

    return labels;
  }

  const flags = flagRollup?.flags ?? [];
  const labels: string[] = [];
  flags.forEach((flag) => {
    const label = flag?.type_label;
    if (label && !labels.includes(label)) {
      labels.push(label);
    }
  });

  return labels;
}
